import { SparklesIcon as SparklesOutlineIcon } from '@heroicons/react/24/outline';
import { FireIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { animated } from '@react-spring/web';
import { useAppSelector } from 'app/hooks';
import SizingAccuracyRing from 'components/layout/sizing-accuracy-ring';
import useAnimatedNumber from 'hooks/use-animated-number';
import { useMemo } from 'react';
import { abbreviateNumber } from 'utils/helpers';
import { getStructureSpacesSquareFootage } from 'utils/space-area';
import { getStructureStats } from 'utils/structure-stats';
import { StructureMetricsFootprintMode } from './structure-metrics-footprint-mode';

type SquareFootageDisplayProps = {
  readonly spacesSquareFootage: number;
  readonly projectSquareFootage: number;
  readonly immediate?: boolean;
};

function SquareFootageDisplay({
  spacesSquareFootage,
  projectSquareFootage,
  immediate = false
}: SquareFootageDisplayProps) {
  const animatedSpaces = useAnimatedNumber(
    spacesSquareFootage,
    abbreviateNumber,
    {
      immediate
    }
  );
  const animatedProject = useAnimatedNumber(
    projectSquareFootage,
    abbreviateNumber
  );

  return (
    <div className='flex min-w-36 flex-col items-center'>
      <span className='pb-1 text-xs font-medium uppercase tracking-wide text-gray-500'>
        Square Footage
      </span>
      <div className='flex items-baseline gap-1'>
        <animated.span className='text-lg font-semibold tabular-nums text-gray-800'>
          {animatedSpaces.display}
        </animated.span>
        <span className='text-sm text-gray-500'>/ </span>
        <animated.span className='text-sm tabular-nums text-gray-500'>
          {animatedProject.display}
        </animated.span>
        <span className='text-sm text-gray-500'>sq ft</span>
      </div>
    </div>
  );
}

type BTUDisplayProps = {
  readonly heating: number;
  readonly cooling: number;
};

function BTUDisplay({ heating, cooling }: BTUDisplayProps) {
  const animatedHeating = useAnimatedNumber(heating, abbreviateNumber);
  const animatedCooling = useAnimatedNumber(cooling, abbreviateNumber);

  return (
    <div className='flex min-w-32 flex-col items-center gap-1'>
      <span className='text-xs font-medium uppercase tracking-wide text-gray-500'>
        BTUs
      </span>
      <div className='flex flex-col items-center'>
        <div className='flex items-center gap-1'>
          <SparklesIcon className='h-4 w-4 text-sky-500' />
          <animated.span className='text-lg font-semibold tabular-nums text-gray-800'>
            {animatedCooling.display}
          </animated.span>
          <span className='text-sm text-gray-500'>cooling</span>
        </div>
        <div className='flex items-center gap-1'>
          <FireIcon className='h-4 w-4 text-orange-500' />
          <animated.span className='text-lg font-semibold tabular-nums text-gray-800'>
            {animatedHeating.display}
          </animated.span>
          <span className='text-sm text-gray-500'>heating</span>
        </div>
      </div>
    </div>
  );
}

type CountStatProps = {
  readonly label: string;
  readonly value: number;
};

function CountStat({ label, value }: CountStatProps) {
  const animatedValue = useAnimatedNumber(value, abbreviateNumber);

  return (
    <div className='flex flex-col items-center'>
      <animated.span className='text-lg font-semibold tabular-nums text-gray-800'>
        {animatedValue.display}
      </animated.span>
      <span className='text-xs text-gray-500'>{label}</span>
    </div>
  );
}

type StructureMetricsDashboardProps = Readonly<{
  footprintMode?: StructureMetricsFootprintMode;
}>;

export default function StructureMetricsDashboard({
  footprintMode
}: StructureMetricsDashboardProps) {
  const { projectSettings } = useAppSelector((state) => state.project);
  const { structure } = useAppSelector((state) => state.structure);
  const { totalBTUs } = useAppSelector((state) => state.project);

  const projectSquareFootage = useMemo(() => {
    const setting = projectSettings.find((ps) => ps.code === 'square_footage');
    if (!setting?.value) return 0;
    const value = Number(setting.value);
    return Number.isNaN(value) ? 0 : value;
  }, [projectSettings]);

  const spacesSquareFootage = useMemo(
    () => getStructureSpacesSquareFootage(structure),
    [structure]
  );

  const currentSquareFootage = footprintMode?.area ?? spacesSquareFootage;

  const stats = useMemo(() => getStructureStats(structure), [structure]);

  return (
    <div className='flex h-full w-56 flex-col items-center gap-10 overflow-y-auto rounded-xl border border-gray-200 bg-white px-4 py-8 shadow-sm'>
      <div className='flex flex-col items-center gap-1'>
        <span className='pb-1 text-xs font-medium uppercase tracking-wide text-gray-500'>
          Sizing Accuracy
        </span>
        <SizingAccuracyRing />
      </div>
      <div className='h-px w-full bg-gray-200' />
      <div className='flex w-full flex-col items-center gap-3'>
        <SquareFootageDisplay
          spacesSquareFootage={currentSquareFootage}
          projectSquareFootage={projectSquareFootage}
          immediate={footprintMode?.immediate ?? false}
        />
        {footprintMode?.onAutoScale && (
          <button
            type='button'
            className='inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-gray-300 bg-white px-2 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            onClick={footprintMode.onAutoScale}
          >
            <SparklesOutlineIcon className='h-5 w-5' />
            Auto-Scale Footprint
          </button>
        )}
      </div>
      <div className='h-px w-full bg-gray-200' />
      <BTUDisplay heating={totalBTUs.heating} cooling={totalBTUs.cooling} />
      <div className='h-px w-full bg-gray-200' />
      <div className='flex w-full flex-col items-center gap-2'>
        <span className='text-xs font-medium uppercase tracking-wide text-gray-500'>
          Structure
        </span>
        <div className='grid w-full grid-cols-2 gap-x-3 gap-y-4'>
          <CountStat label='Levels' value={stats.levels} />
          <CountStat label='Spaces' value={stats.rooms} />
          <CountStat label='Windows' value={stats.windows} />
          <CountStat label='Doors' value={stats.doors} />
        </div>
      </div>
    </div>
  );
}
